import React, { useState } from 'react';

import { Clear, SelectAll } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';

import ActionButton from './ActionButton';
import { classes } from './styles';

import type { Dispatch } from 'react';

import type { TableActionButtonProps, TableActionDrawerAction } from './types';

// The parentDiv prop is used to center the tooltip in relation to the table and not the
// entire page. You can pass in a ref.current which should be a parent div with 100% width

const TotalSelectedIndicatorWithActions = ({
  totalSelected,
  parentDiv,
  dispatch = () => {},
  actionButtonsList = [],
  actionCall = () => {},
  actionButtonId = '',
  onSelectAll = () => {},
  clearAllEnabled = false,
  totalItems = 0,
  canSelectAll = false
}: {
  totalSelected: number;
  parentDiv: HTMLDivElement | null;
  dispatch?: Dispatch<TableActionDrawerAction>;
  actionButtonsList?: TableActionButtonProps[];
  actionCall?: null | (() => void);
  actionButtonId?: string;
  onSelectAll?: () => void | Promise<void>;
  clearAllEnabled?: boolean;
  totalItems?: number;
  canSelectAll?: boolean;
}): React.JSX.Element => {
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const handleSelectAll = async () => {
    if (!onSelectAll || isSelectingAll) return;

    setIsSelectingAll(true);
    try {
      await onSelectAll();
    } catch {
      // Selection failures are handled by upstream API error handling.
    } finally {
      setIsSelectingAll(false);
    }
  };

  return (
    <Box
      id="selected-tooltip"
      sx={{
        ...classes.selectedTooltip,
        left: parentDiv
          ? parentDiv.getBoundingClientRect().left +
            parentDiv.getBoundingClientRect().width / 2 -
            350 // this is half the size of the 'tooltip' div
          : 55, // this value for when the parent div is inside a modal
        ...(totalSelected ? classes.fadeIn : classes.fadeOut)
      }}
    >
      <Box id="selected-tooltip-text" sx={classes.selectAllWithActionsTooltip}>
        {totalSelected} selected
        <Box>
          {clearAllEnabled ? (
            <Button
              disabled={isSelectingAll}
              onClick={() => dispatch?.({ type: 'REMOVE_ALL_SELECTED_ITEMS' })}
              startIcon={<Clear />}
              sx={{
                ...classes.selectAllWithActionsTooltipButton,
                marginLeft: '5px',
                color: '#A15957',
                background: 'none',
                border: 'none',
                '&:hover': {
                  background: 'none',
                  border: 'none'
                }
              }}
            >
              Clear
            </Button>
          ) : null}
          {actionButtonsList && dispatch
            ? actionButtonsList.map((action) => (
                <ActionButton
                  key={action.id}
                  action={action}
                  actionButtonId={actionButtonId}
                  actionCall={actionCall}
                  disabled={isSelectingAll}
                  dispatch={dispatch}
                />
              ))
            : null}
          {onSelectAll && canSelectAll ? (
            <Button
              disabled={isSelectingAll}
              onClick={handleSelectAll}
              startIcon={
                isSelectingAll ? <CircularProgress color="inherit" size={14} /> : <SelectAll />
              }
              sx={{
                ...classes.selectAllWithActionsTooltipButton,
                marginLeft: '5px'
              }}
            >
              {isSelectingAll ? 'Selecting all...' : `Select all ${totalItems} items`}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default TotalSelectedIndicatorWithActions;
